<!-- App.vue -->
<script setup>
import { ref, computed, watch, provide, onMounted, onBeforeUnmount, readonly } from 'vue';
import * as Sentry from '@sentry/vue';
import { useRoute } from 'vue-router';
import Navbar from '@/components/Navbar.vue';
import Footer from '@/components/Footer.vue';
import StatusBar from '@/components/StatusBar.vue';
import { backendUrl } from '@/config/backend';
import { useSchoolContext, getStoredToken } from '@/config/schools';
import { useAdminContext } from '@/config/adminApi';

// Theme management
const theme = ref(localStorage.getItem('theme') || 'dark');

const setTheme = (newTheme) => {
  theme.value = newTheme;
  localStorage.setItem('theme', newTheme);
};

provide('theme', theme);
provide('setTheme', setTheme);

const { activeSchoolId, activeSchool, syncActiveSchool } = useSchoolContext();

const syncSchoolContext = () => syncActiveSchool({ token: getStoredToken() });
syncSchoolContext();

provide('schoolId', activeSchoolId);
provide('school', activeSchool);
provide('syncSchoolContext', syncSchoolContext);

// Admin roles (is_admin / is_superadmin) — fetched once per token, drives the
// navbar Admin link and the dashboard.
const { adminRoles, fetchAdminRoles } = useAdminContext();
if (getStoredToken()) {
  fetchAdminRoles();
}
provide('adminRoles', adminRoles);

watch(theme, (newTheme) => {
  if (newTheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
});

// Apply initial theme
if (theme.value === 'dark') {
  document.documentElement.classList.add('dark');
}

const schoolFavicons = {
  collegiate: '/favicon.ico',
  grace: '/logo_256_square_RIt_icon.ico',
};

watch(activeSchoolId, (schoolId) => {
  Sentry.setTag('schoolId', schoolId);

  const favicon = schoolFavicons[schoolId];
  if (favicon) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = favicon;
  }
}, { immediate: true });

// Status bar management
const statusBar = ref({
  isVisible: false,
  message: '',
  type: 'info',
  dismissible: true,
  messageId: null
});

const buildStatusBarUrl = () => {
  const url = new URL(backendUrl('/admin/status-bar'));
  if (activeSchoolId.value) {
    url.searchParams.set('schoolId', activeSchoolId.value);
  }
  return url.toString();
};

const fetchStatusBar = async () => {
  try {
    const token = getStoredToken();
    const response = await fetch(buildStatusBarUrl(), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (response.ok) {
      const data = await response.json();
      statusBar.value = data;
    }
  } catch (error) {
    console.error('Failed to fetch status bar:', error);
  }
};

provide('statusBar', readonly(statusBar));
provide('refreshStatusBar', fetchStatusBar);

const route = useRoute();

const showNavbar = computed(() => {
  const facultyRouteNames = ['FacultyToday', 'FacultySection'];
  return route.name !== 'Landing'
      && route.name !== 'Privacy'
      && route.name !== 'Terms'
      && !facultyRouteNames.includes(route.name);
});

const showFooter = computed(() => {
  return route.name !== 'Resources';
});

const showStatusBar = computed(() => {
  const allowedRoutes = ['Dashboard', 'Assignments', 'Schedule', 'ClassPage', 'Menu'];
  return allowedRoutes.includes(route.name);
});

watch(() => route.fullPath, () => {
  syncSchoolContext();
});

watch([showStatusBar, activeSchoolId], ([shouldShowStatusBar]) => {
  if (shouldShowStatusBar) {
    fetchStatusBar();
  }
}, { immediate: true });

let statusBarPollInterval = null;

const handleWindowContextSync = () => {
  syncSchoolContext();
  if (getStoredToken()) {
    fetchAdminRoles(); // no-op unless the token changed (token-keyed cache)
  }
  if (showStatusBar.value) {
    fetchStatusBar();
  }
};

onMounted(() => {
  window.addEventListener('focus', handleWindowContextSync);
  window.addEventListener('storage', handleWindowContextSync);

  statusBarPollInterval = window.setInterval(() => {
    if (showStatusBar.value) {
      fetchStatusBar();
    }
  }, 5 * 60 * 1000);
});

onBeforeUnmount(() => {
  window.removeEventListener('focus', handleWindowContextSync);
  window.removeEventListener('storage', handleWindowContextSync);

  if (statusBarPollInterval) {
    window.clearInterval(statusBarPollInterval);
  }
});
</script>

<template>
  <div
      id="app"
      class="min-h-screen flex flex-col bg-slate-100 text-black dark:bg-gradient-to-r dark:from-gray-900 dark:via-black dark:to-gray-900 dark:text-white font-poppins"
>
  <Navbar v-if="showNavbar" />
  <StatusBar v-if="showStatusBar" :statusBar="statusBar" />
    <router-view />
    <Footer v-if="showFooter" class="mt-auto" />
  </div>
</template>

<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;800&display=swap');

.font-poppins {
  font-family: 'Poppins', sans-serif;
}
</style>
